'use client'

import { useEffect, useMemo, useState } from 'react'
import { IconBookmark, IconBook2 } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { LoginDialog } from '@/components/auth/login-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { useAuth } from '@/hooks/useAuth'
import { analytics } from '@/lib/analytics'
import { getReaderStudyCandidates } from '@/lib/reader/study-candidates'
import { areWordsSaved, saveWordToDefaultDeck } from '@/lib/study'
import { cn } from '@/lib/utils'
import type { ReaderContent } from '@/types/reader'

export function ReaderStudyTextDialog({ content }: { content: ReaderContent }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const entryIds = useMemo(() => {
    const ids = new Set<number>()
    content.blocks.forEach((block) => {
      block.tokens.forEach((token) => {
        if (token.entryId && content.glossary[String(token.entryId)]) {
          ids.add(token.entryId)
        }
      })
    })
    return [...ids]
  }, [content])

  const { data: savedMap = new Map<number, boolean>(), isLoading } = useQuery({
    queryKey: ['reader-study-saved-words', user?.id, entryIds.join(',')],
    queryFn: () => areWordsSaved(entryIds),
    enabled: Boolean(user?.id && entryIds.length),
    staleTime: 1000 * 60 * 5
  })

  const candidates = useMemo(
    () => getReaderStudyCandidates(content, savedMap),
    [content, savedMap]
  )

  useEffect(() => {
    if (!open) return
    setSelectedIds(
      new Set(
        candidates
          .filter((candidate) => candidate.selected)
          .map((candidate) => candidate.entry.id)
      )
    )
  }, [candidates, open])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const ids = [...selectedIds]
      await Promise.all(ids.map((id) => saveWordToDefaultDeck(id)))
      return ids
    },
    onSuccess: (ids) => {
      analytics.readerTextWordsSaved(content.sourceType, ids.length)
      toast.success(
        ids.length === 1
          ? 'Saved 1 word to Saved Words'
          : `Saved ${ids.length} words to Saved Words`
      )
      queryClient.invalidateQueries({ queryKey: ['all-collections', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['bulk-bookmarks'] })
      queryClient.invalidateQueries({ queryKey: ['bookmark'], exact: false })
      queryClient.invalidateQueries({
        queryKey: ['reader-study-saved-words', user?.id]
      })
      setOpen(false)
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Could not save words'
      )
    }
  })

  const trigger = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="shrink-0"
      onClick={() => {
        analytics.readerStudyTextClicked(content.sourceType, candidates.length)
      }}
    >
      <IconBook2 className="h-4 w-4" />
      <span className="hidden sm:inline">Study text</span>
      <span className="sm:hidden">Study</span>
    </Button>
  )

  if (!user?.id) {
    return <LoginDialog>{trigger}</LoginDialog>
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[min(720px,calc(100dvh-2rem))] overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="border-b px-5 py-4">
          <DialogTitle>Study this text</DialogTitle>
          <DialogDescription>
            Save a few suggested words from this text for later review.
          </DialogDescription>
        </DialogHeader>
        <p className="text-muted-foreground border-b px-5 py-3 text-sm leading-6">
          Tap words as you read. Use this list when you want to turn the text
          into a small review queue.
        </p>
        <div className="max-h-[46dvh] overflow-y-auto px-2 py-2">
          {isLoading ? (
            <p className="text-muted-foreground px-3 py-8 text-center text-sm">
              Checking saved words...
            </p>
          ) : candidates.length ? (
            <div className="space-y-1">
              {candidates.map((candidate) => {
                const checked = selectedIds.has(candidate.entry.id)
                return (
                  <div
                    key={candidate.entry.id}
                    role="button"
                    tabIndex={candidate.isSaved ? -1 : 0}
                    className={cn(
                      'hover:bg-accent flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                      candidate.isSaved && 'opacity-55'
                    )}
                    onClick={() => {
                      if (candidate.isSaved) return
                      setSelectedIds((current) => {
                        const next = new Set(current)
                        if (next.has(candidate.entry.id)) {
                          next.delete(candidate.entry.id)
                        } else {
                          next.add(candidate.entry.id)
                        }
                        return next
                      })
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      event.currentTarget.click()
                    }}
                  >
                    <Checkbox
                      checked={candidate.isSaved || checked}
                      disabled={candidate.isSaved}
                      className="mt-1"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-semibold">
                          {candidate.entry.simplified}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {candidate.entry.pinyin}
                        </span>
                      </span>
                      <span className="text-muted-foreground mt-1 line-clamp-1 block text-xs">
                        {candidate.entry.definitions[0] || 'Suggested word'}
                      </span>
                    </span>
                    <span className="text-muted-foreground shrink-0 text-xs">
                      {candidate.isSaved
                        ? 'saved'
                        : `${candidate.occurrences}x`}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-muted-foreground px-3 py-8 text-center text-sm">
              No suggested words found in this text yet.
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2 border-t p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground text-sm">
            {selectedIds.size} selected
          </p>
          <Button
            disabled={!selectedIds.size || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            <IconBookmark className="h-4 w-4" />
            Save selected
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
